// 配合比定義 ID から、明細を派生してレシピラベルを生成するユーティリティ。
// MixRatioMasterPage のアコーディオン要約、WorkClassificationPage の配合比表示で使う。
//
// 例: "主剤 1 : 硬化剤 0.12〜0.15"

import type { MixRatioDefinitionBase } from '../schema/generated/MixRatioDefinition';
import type { ExtendedDB } from '../types/extendedDB';

/**
 * 配合比定義 ID から、派生レシピラベルを生成する。
 * 明細が無ければ空文字を返す。
 */
export function deriveMixRatioLabel(db: ExtendedDB, defId: number | string | null | undefined): string {
  if (defId == null) return '';
  const details = (db.mixRatioDetails ?? []).filter((d) =>
    d.deleted_at == null && String(d.mix_ratio_definition_id) === String(defId)
  );
  if (details.length === 0) return '';
  const parts: string[] = [];
  const detailNameById = new Map<string, string>();
  for (const md of db.materialDetails ?? []) {
    detailNameById.set(String(md.id), md.detail_name ?? '');
  }
  for (const d of details) {
    const name = detailNameById.get(String(d.material_detail_id)) ?? '?';
    let ratio: string;
    if (d.ratio_end != null && d.ratio_end !== d.ratio_start) {
      ratio = `${d.ratio_start}〜${d.ratio_end}`;
    } else {
      ratio = String(d.ratio_start);
    }
    parts.push(`${name} ${ratio}`);
  }
  return parts.join(' : ');
}

/**
 * MixRatioDefinition を見やすいラベルに変換。
 *   - 派生レシピがあればそれを返す
 *   - 無ければ「配合比 #ID」を返す (フォールバック)
 *   - 「材料なし」の場合は明示
 */
export function mixRatioDisplayName(db: ExtendedDB, def: MixRatioDefinitionBase | null | undefined): string {
  if (!def) return '-';
  const recipe = deriveMixRatioLabel(db, def.id);
  if (recipe) return recipe;
  const mat = db.materials.find((m) => String(m.id) === String(def.material_id));
  if (mat && mat.name === '材料なし') return '材料なし';
  return `配合比 #${def.id}`;
}
